import GamePreferences from '@/components/game-play/GamePreferences';
import { Game as GameType } from '@/types/game';
import { GamePlayer } from '@/types/golf';
import { api } from '@/utils/api';
import { supabase } from '@/utils/supabase';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonLoading,
  IonSpinner,
  IonTitle,
  IonToolbar,
  useIonRouter,
  useIonToast
} from '@ionic/react';
import { chevronForward, closeOutline, optionsOutline, peopleOutline, settingsOutline } from 'ionicons/icons';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import GameSetup from '../game-setup/GameSetup';
import GameSetupPlayers from '../game-setup/GameSetupPlayers';

interface GameSettingsContentProps {
  gameId: string;
  onClose: () => void; // To close the modal
}

const GameSettingsContent: React.FC<GameSettingsContentProps> = ({ gameId, onClose }) => {
  const { t } = useTranslation();
  const [presentToast] = useIonToast();
  const router = useIonRouter();
  const [players, setPlayers] = useState<GamePlayer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeView, setActiveView] = useState<'menu' | 'game-settings' | 'players' | 'preferences'>('menu');
  const [gameData, setGameData] = useState<GameType | null>(null);

  const fetchGameData = useCallback(async () => {
    try {
      const response = await api.get<GameType>(`/game/${gameId}`);
      const data = response.data;
      setGameData(data);
      if (data.players) {
        setPlayers(data.players);
      }
      return data;
    } catch (error) {
      console.error('Error loading game data:', error);
      presentToast({
        message: t('errors.failedToLoadGame'),
        duration: 3000,
        position: 'bottom',
        color: 'danger',
      });
      throw error;
    }
  }, [gameId, presentToast, t]);

  const loadGameData = async () => {
    try {
      setIsLoading(true);
      await fetchGameData();
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadGameData();
  }, [gameId]);

  const handleRefreshGameData = useCallback(async () => {
    try {
      setIsRefreshing(true);
      await fetchGameData();
      presentToast({
        message: t('game.gameDataRefreshed'),
        duration: 2000,
        position: 'bottom',
        color: 'success',
      });
    } catch (error) {
      console.error('Error refreshing game data:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [fetchGameData, presentToast, t]);

  useEffect(() => {
    const subscription = supabase
      .channel(`game_settings_updates_${gameId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'game_settings_updates',
        filter: `game_id=eq.${gameId}`,
      }, () => {
        handleRefreshGameData();
      })
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [gameId, handleRefreshGameData]);

  const handleUpdateSuccess = () => {
    presentToast({
      message: t('settings_saved'),
      duration: 2000,
      color: 'success'
    });
    handleRefreshGameData();
    setActiveView('menu'); // Go back to menu view within the modal
  };

  const handleEditPlayer = (player: GamePlayer) => {
    // Use playerId instead of id if available
    const playerId = 'playerId' in player ? player.playerId : player.id;
    if (playerId) {
      router.push(`/game/${gameId}/player/${playerId}/edit`, 'forward', 'push');
    }
  };

  const handleRemovePlayer = async (playerId: string) => {
    try {
      await api.delete(`/game/${gameId}/player/${playerId}`);
      handleRefreshGameData();
    } catch (error) {
      console.error('Error removing player:', error);
      presentToast({
        message: t('errors.failedToRemovePlayer'),
        duration: 3000,
        color: 'danger'
      });
    }
  };

  // Helper to get course data in the expected format
  const getCourseData = () => {
    if (!gameData?.course) return undefined;

    // Create a new course object with all required fields
    const course = {
      ...gameData.course,
      id: typeof gameData.course.id === 'string' ? parseInt(gameData.course.id, 10) : gameData.course.id,
      name: gameData.course.name || 'Unknown Course',
      // Add required fields with defaults
      facilityId: gameData.course.id?.toString() || '',
      slug: gameData.course.name?.toLowerCase().replace(/\s+/g, '-') || 'unknown-course',
      guestPolicy: 'allow' as const,
      // Ensure optional fields are included
      city: gameData.course.city || '',
      country: gameData.course.country || '',
      state: gameData.course.state || '',
      phone: gameData.course.phone || '',
      website: gameData.course.website || '',
      description: gameData.course.description || '',
      thumbnail: gameData.course.thumbnail || '',
      latlon: gameData.course.latlon || null
    };

    return course;
  };

  const renderContent = () => {
    if (isLoading || !gameData) {
      return (
        <div className="flex items-center justify-center h-full">
          <IonSpinner name="crescent" />
        </div>
      );
    }

    switch (activeView) {
      case 'game-settings':
        const courseData = getCourseData();
        return (
          <GameSetup
            gameId={gameId}
            initialData={{
              ...gameData,
              course: courseData,
              // Add any other required fields with defaults if needed
            }}
            onSuccess={handleUpdateSuccess}
            onBack={() => setActiveView('menu')}
          />
        );

      case 'players':
        return (
          <>
            <IonHeader>
              <IonToolbar>
                <IonButtons slot="start">
                  <IonButton onClick={() => setActiveView('menu')}>{t('back')}</IonButton>
                </IonButtons>
                <IonTitle>{t('players')}</IonTitle>
              </IonToolbar>
            </IonHeader>
            <IonContent>
              {gameData?.players && (
                <GameSetupPlayers
                  players={gameData.players}
                  initialData={gameData}
                  gameId={gameId}
                  course={gameData.course}
                  onUpdateSuccess={handleRefreshGameData}
                  isGameSettings={true}
                />
              )}
            </IonContent>
          </>
        );

      case 'preferences':
        // Assuming GamePreferences.tsx exists and is correctly implemented
        return (
          <>
            <IonHeader>
              <IonToolbar>
                <IonButtons slot="start">
                  <IonButton onClick={() => setActiveView('menu')}>{t('back')}</IonButton>
                </IonButtons>
                <IonTitle>{t('preferences')}</IonTitle>
              </IonToolbar>
            </IonHeader>
            <IonContent className="ion-padding">
              <GamePreferences
                gameId={gameId}
                preferences={gameData?.preferences}
                onUpdateSuccess={handleUpdateSuccess}
              />
            </IonContent>
          </>
        );

      default: // Menu
        return (
          <>
            <IonHeader className="ion-no-border">
              <IonToolbar color="light">
                <IonButtons slot="end">
                  <IonButton onClick={() => onClose()}><IonIcon icon={closeOutline} /></IonButton>
                </IonButtons>
                <IonTitle>{t('settings')}</IonTitle>
              </IonToolbar>
            </IonHeader>
            <IonContent color="light" className='ion-padding'>
              <IonList className='bg-card rounded-xl'>
                <IonItem button onClick={() => setActiveView('game-settings')} detail={false} className="rounded-lg mb-2">
                  <IonIcon icon={settingsOutline} slot="start" className="text-xl" />
                  <IonLabel>{t('game_settings')}</IonLabel>
                  <IonIcon icon={chevronForward} slot="end" color="medium" />
                </IonItem>
                <IonItem button onClick={() => setActiveView('players')} detail={false} className="rounded-lg mb-2">
                  <IonIcon icon={peopleOutline} slot="start" className="text-xl" />
                  <IonLabel>{t('players')}</IonLabel>
                  <IonIcon icon={chevronForward} slot="end" color="medium" />
                </IonItem>
                <IonItem button onClick={() => setActiveView('preferences')} detail={false} className="rounded-lg">
                  <IonIcon icon={optionsOutline} slot="start" className="text-xl" />
                  <IonLabel>{t('preferences')}</IonLabel>
                  <IonIcon icon={chevronForward} slot="end" color="medium" />
                </IonItem>
              </IonList>
            </IonContent>
          </>
        );
    }
  };

  return (
    <>
      {isRefreshing && <IonLoading isOpen={isRefreshing} message={t('refreshing_data') || 'Refreshing...'} />}
      {renderContent()}
    </>
  );
};

export default GameSettingsContent;
